import { useState } from 'react'
import { FindParkingBrowser } from '@parkify/find-parking'
import { API_BASE } from '../api'
import { LoginSlideOver } from '../components/LoginSlideOver'

/**
 * Find parking — the no-QR browse experience (UX-redesign slice 5).
 *
 * The browse surface itself (schematic/Google map, nearby-zone list, search,
 * geolocation, checkout hand-off) now lives in the shared @parkify/find-parking
 * package so the consumer checkout's no-zone screen reuses the exact same map.
 * This screen keeps only the portal-specific chrome around it: the signed-in
 * status row and the in-page login slide-over (the phone-OTP sign-in re-skinned)
 * that never navigates away from the browse page.
 *
 * Hand-off stays plain URL navigation to pay.parkify.com (`/?z=<id>` to start,
 * `/subscribe?z=<id>` for a monthly permit) — we never rebuild card capture here.
 *
 * `signedIn` is owned by App (the router), not local state: signing out happens
 * in the header, and this screen stays mounted across a sign-out on /find, so a
 * local copy would go stale and keep claiming "Signed in". App re-derives it
 * from the token on every auth change and passes it down.
 */

const PAY_BASE = (import.meta.env.VITE_PAY_BASE as string | undefined) ?? 'https://pay.parkify.com'

// When set (repo build secret VITE_GOOGLE_MAPS_API_KEY — the same key ops uses
// for the zone address picker), the browse map upgrades from the hand-drawn
// schematic to a real Google map. Absent, or if the SDK fails to load at
// runtime, it falls back to the schematic (handled inside the shared component).
const MAPS_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || undefined

// onAccount is REQUIRED (not optional): omitting it would silently fall back to
// the bare-`#/`-link no-op this screen exists to avoid, so a future caller that
// forgets it should fail at compile time, not degrade to a dead "My account".
export function FindParking({ signedIn, onAuthed, onAccount }: { signedIn: boolean; onAuthed?: () => void; onAccount: () => void }) {
  const [loginOpen, setLoginOpen] = useState(false)

  const statusRow = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12 }}>
      <span style={{
        width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
        background: signedIn ? 'var(--color-success-bg)' : 'var(--color-gray-200)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }} aria-hidden>
        {signedIn ? (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)"
            strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        ) : (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--color-gray-600)"
            strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3" /><path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6" /></svg>
        )}
      </span>
      <span style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 600 }}>
        {signedIn ? 'Signed in' : 'Not signed in'}
      </span>
      {/* The separator is its own muted element, NOT part of the link/button
          label: baked into the string (as the demo does it) it renders purple,
          bold and clickable, and screen readers announce it as link text. Same
          shape as the consumer's `.pk-idrow__sep` (parkify-pay #1712). */}
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-500)' }} aria-hidden>·</span>
      {signedIn ? (
        // href stays for progressive-enhancement / open-in-new-tab, but the
        // onClick is what reliably navigates: a bare `#/` link is a no-op when
        // the hash is already `#/` (fires no hashchange → router never updates).
        <a
          href="#/"
          onClick={(e) => { e.preventDefault(); onAccount() }}
          style={{ fontSize: 12, fontWeight: 700 }}
        >My account →</a>
      ) : (
        <button type="button" onClick={() => setLoginOpen(true)}
          style={{ color: 'var(--color-brand)', fontWeight: 700, fontSize: 12 }}>
          Log in
        </button>
      )}
    </div>
  )

  return (
    <div className="fadein">
      {statusRow}

      <FindParkingBrowser payBase={PAY_BASE} apiBase={API_BASE} mapsApiKey={MAPS_KEY} />

      <LoginSlideOver open={loginOpen} onClose={() => setLoginOpen(false)}
        onSignedIn={() => { setLoginOpen(false); onAuthed?.() }} />
    </div>
  )
}
